import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { CheckCircle, EnvelopeSimple, Eye, FilePdf, PencilSimple, Plus, UserPlus, Warning, X } from "@phosphor-icons/react";
import { VersandDialog } from "../components/Versand";
import { Unterschriften } from "../components/Unterschrift";
import { aender, api, lies, schick, weg } from "../api/client";
import type { Begehung, BegehungErgebnis, Schreiben } from "../api/types";
import { Blur, Dialog, Feld, Meldung } from "../components/Bausteine";
import { Auswahl } from "../components/Auswahl";
import { useBeteiligte } from "./projekt/BeteiligteReiter";
import { TEILNAHME } from "./BesprechungSeite";
import { BEGEHUNG_ART, BEGEHUNG_GRUNDLAGE, ERGEBNIS, MANGEL_STATUS, datum } from "../lib/format";
import { NeuerMangel, useLeistungen } from "./projekt/MaengelReiter";
import { LeistungenWahl } from "./projekt/BegehungenReiter";
import { FotoLeiste } from "../components/FotoLeiste";
import { usePfad } from "../lib/pfad";
import { useProjekt } from "./projekt/ProjektSeite";

/** Abnahme, Zustandsfeststellung, Begehung (DECISIONS E11). */
export function BegehungSeite() {
  const { begehungId = "" } = useParams();
  const qc = useQueryClient();
  const schluessel = ["begehung", begehungId];
  const g = useQuery({ queryKey: schluessel, queryFn: () => lies<Begehung>(`/begehungen/${begehungId}`) });
  const projekt = useProjekt(g.data?.projekt_id).data;
  usePfad(projekt && g.data ? [
    { titel: projekt.name, nach: `/p/${projekt.kurz}/besprechungen` },
    { titel: "Abnahmen & Begehungen", nach: `/p/${projekt.kurz}/begehungen` },
    { titel: BEGEHUNG_ART[g.data.art] ?? "Begehung" },
  ] : []);
  const [mangel, setMangel] = useState(false);
  const [abschluss, setAbschluss] = useState(false);
  const [versand, setVersand] = useState(false);
  const [leistungenWahl, setLeistungenWahl] = useState(false);
  // Abgelegtes Original statt Neudruck (E10), wie beim Protokoll.
  const ausgang = useQuery({
    queryKey: ["schreiben", g.data?.projekt_id],
    queryFn: () => lies<Schreiben[]>(`/projekte/${g.data!.projekt_id}/schreiben`),
    enabled: g.data?.status === "abgeschlossen",
  });
  const abgelegt = ausgang.data?.find((s) => s.begehung_id === begehungId);
  const nachholen = useMutation({
    mutationFn: () => schick(`/begehungen/${begehungId}/schreiben`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schreiben", g.data?.projekt_id] }),
  });
  const aendern = useMutation({
    mutationFn: (r: Partial<Begehung>) => aender<Begehung>(`/begehungen/${begehungId}`, r),
    onSuccess: (neu) => qc.setQueryData(schluessel, { ...neu, meine_rolle: g.data?.meine_rolle }),
  });

  if (g.error) return <main className="seite"><Meldung fehler={g.error} /></main>;
  if (!g.data) return null;
  const b = g.data;
  const offen = b.status === "entwurf" && b.meine_rolle !== "lesen";
  const abnahme = b.art === "abnahme" || b.art === "teilabnahme";
  const ergebnisse: BegehungErgebnis[] = abnahme ? ["abgenommen", "abgenommen_vorbehalt", "verweigert"] : ["festgestellt"];

  return (
    <main className="seite">
      <div className="seite-kopf">
        <div>
          <h1>{BEGEHUNG_ART[b.art]}</h1>
          <div className="dim">
            {[BEGEHUNG_GRUNDLAGE[b.art],
              ...(b.leistungen.length === 1 ? [b.leistung?.bezeichnung, b.leistung?.an] : []),
              b.leistungen.length > 1 && `${b.leistungen.length} Leistungen`,
              datum(b.datum), b.ort].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="luft" />
        {b.status === "entwurf" ? <span className="chip warn">Entwurf</span> : <span className="chip gut">abgeschlossen</span>}
      </div>

      {b.hinweise.length > 0 && (
        <div className="hinweise">
          {b.hinweise.map((h) => <div key={h} className="hinweis"><Warning size={15} /> {h}</div>)}
        </div>
      )}

      {(b.leistungen.length > 0 || offen) && (
        <section className="karte">
          <div className="karte-kopf">
            <h2>{b.leistungen.length === 1 ? "Leistung" : "Leistungen"}</h2>
            <div className="luft" />
            {offen && <button className="knopf" onClick={() => setLeistungenWahl(true)}><PencilSimple size={16} /> Ändern</button>}
          </div>
          <div className="liste">
            {b.leistungen.map((l) => (
              <div key={l.id} className="zeile" style={{ minHeight: 44, padding: "6px 4px" }}>
                <div className="haupt"><div>{l.bezeichnung}</div>{l.an && <div className="mute">{l.an}</div>}</div>
                <span className="mute">Mängelfrist {l.maengelfrist_monate} Monate</span>
              </div>
            ))}
            {b.leistungen.length === 0 && <div className="leer">Keine Leistung zugeordnet.</div>}
          </div>
        </section>
      )}

      <section className="karte">
        <div className="raster">
          <label className="feld"><span>Datum</span>
            <input type="date" className="eingabe" disabled={!offen} defaultValue={b.datum}
                   onBlur={(e) => e.target.value !== b.datum && aendern.mutate({ datum: e.target.value })} /></label>
          <label className="feld"><span>Ort</span><Blur disabled={!offen} wert={b.ort} speichern={(v) => aendern.mutate({ ort: v })} /></label>
          <label className="feld breit"><span>Gegenstand</span>
            <Blur mehrzeilig disabled={!offen} wert={b.gegenstand} speichern={(v) => aendern.mutate({ gegenstand: v })} /></label>
          <label className="feld breit"><span>Feststellungen</span>
            <Blur mehrzeilig disabled={!offen} wert={b.feststellungen}
                  placeholder={abnahme ? "z. B. Leistung im Wesentlichen vertragsgemäß fertiggestellt" : "Was ist gemeinsam festgestellt worden?"}
                  speichern={(v) => aendern.mutate({ feststellungen: v })} /></label>
        </div>
        {/* Uebersichtsfotos der Begehung; Fotos einzelner Maengel stehen beim Mangel. */}
        <FotoLeiste projektId={b.projekt_id} ziel="begehung_id" zielId={b.id} kann={offen} />
      </section>

      <Anwesende b={b} offen={offen} neuLaden={() => qc.invalidateQueries({ queryKey: schluessel })} />

      <section className="karte">
        <div className="karte-kopf">
          <h2>Mängel</h2>
          <div className="luft" />
          {offen && <button className="knopf" onClick={() => setMangel(true)}><Plus size={16} weight="bold" /> Mangel</button>}
        </div>
        <div className="liste">
          {b.maengel.map((m) => (
            <div key={m.id} className="zeile">
              <span className="nr">M-{String(m.lfd).padStart(3, "0")}</span>
              <div className="haupt"><div>{m.lage && `${m.lage}: `}{m.beschreibung}</div>
                <div className="mute">{[b.leistungen.length > 1 && b.leistungen.find((l) => l.id === m.leistung_id)?.bezeichnung,
                                        m.frist && `Frist ${datum(m.frist)}`].filter(Boolean).join(" · ")}</div>
                {/* Maengel bleiben auch nach dem Abschluss bebilderbar (Beseitigung belegen). */}
                <FotoLeiste projektId={b.projekt_id} ziel="mangel_id" zielId={m.id} kann={b.meine_rolle !== "lesen"} /></div>
              <span style={{ display: "flex", gap: 6 }}>
                {m.wesentlich && <span className="chip fehler">wesentlich</span>}
                <span className="chip">{MANGEL_STATUS[m.status]}</span>
              </span>
            </div>
          ))}
          {b.maengel.length === 0 && <div className="leer">Keine Mängel festgestellt.</div>}
        </div>
      </section>

      <section className="karte">
        <div className="karte-kopf"><h2>Unterschriften</h2>
          <span className="mute">auf dem Tablet, vor dem Abschließen</span></div>
        <Unterschriften begehungId={b.id} felder={b.unterschriften} offen={offen}
                        geaendert={() => qc.invalidateQueries({ queryKey: schluessel })} />
      </section>

      <section className="karte">
        <div className="karte-kopf"><h2>Ergebnis</h2></div>
        <div className="punkt-leiste">
          <div className="wahl" role="group" aria-label="Ergebnis">
            {ergebnisse.map((e) => (
              <button key={e} disabled={!offen} className={b.ergebnis === e ? "an" : ""}
                      onClick={() => aendern.mutate({ ergebnis: e })}>{ERGEBNIS[e]}</button>
            ))}
          </div>
        </div>
        {abnahme && (
          <div className="raster" style={{ marginTop: 12 }}>
            <label className="haken breit">
              <input type="checkbox" disabled={!offen} checked={b.vorbehalt_vertragsstrafe}
                     onChange={(e) => aendern.mutate({ vorbehalt_vertragsstrafe: e.target.checked })} />
              Vorbehalt der Vertragsstrafe (§ 11 Abs. 4 VOB/B)
            </label>
            {b.ergebnis !== "verweigert" && (
              <label className="feld"><span>Abnahme wirkt ab</span>
                {/* Beim Verlassen, nicht bei jedem Tastendruck (Zwischendaten beim Tippen des Jahres). */}
                <input key={b.abnahme_wirkt_ab ?? ""} type="date" className="eingabe" disabled={!offen}
                       defaultValue={b.abnahme_wirkt_ab ?? ""}
                       onBlur={(e) => (e.target.value || null) !== b.abnahme_wirkt_ab &&
                         aendern.mutate({ abnahme_wirkt_ab: e.target.value || null })} /></label>
            )}
            {b.maengel.length > 0 && b.ergebnis !== "verweigert" && (
              <p className="mute breit">Die {b.maengel.length} aufgeführten Mängel werden in der Niederschrift vorbehalten (§ 640 Abs. 3 BGB).</p>
            )}
          </div>
        )}
        <Meldung fehler={aendern.error} />
      </section>

      <div className="leiste-unten">
        <a className="knopf" href={`/api/begehungen/${b.id}/druck`} target="_blank" rel="noreferrer"><Eye size={16} /> Druckansicht</a>
        {b.status === "entwurf"
          ? <a className="knopf" href={`/api/begehungen/${b.id}/pdf`} target="_blank" rel="noreferrer"><FilePdf size={16} /> PDF (Entwurf)</a>
          : abgelegt
            ? <a className="knopf" href={`/api/schreiben/${abgelegt.id}/datei`} target="_blank" rel="noreferrer"><FilePdf size={16} /> PDF</a>
            : ausgang.isSuccess && b.meine_rolle === "leitung" &&
              <button className="knopf" disabled={nachholen.isPending} onClick={() => nachholen.mutate()}><FilePdf size={16} /> Ablage nachholen</button>}
        <Meldung fehler={nachholen.error} />
        <div className="luft" />
        {abgelegt && b.meine_rolle === "leitung" && (
          <button className={`knopf${abgelegt.verschickt ? "" : " haupt"}`} onClick={() => setVersand(true)}>
            <EnvelopeSimple size={16} /> {abgelegt.verschickt ? `Versendet an ${abgelegt.verschickt}` : "Niederschrift versenden"}
          </button>
        )}
        {versand && abgelegt && <VersandDialog schreibenId={abgelegt.id} titel={abgelegt.titel} zu={() => setVersand(false)} />}
        {b.status === "entwurf" && b.meine_rolle === "leitung" && (
          <button className="knopf haupt" disabled={b.ergebnis === "offen"} onClick={() => setAbschluss(true)}>
            <CheckCircle size={16} /> Abschließen
          </button>
        )}
      </div>

      {/* Bei mehreren Leistungen wählt die Erfassung, zu welcher der Mangel gehört. */}
      <NeuerMangel key={b.leistungen.map((l) => l.id).join()} projekt={{ id: b.projekt_id }} leistungen={b.leistungen.length > 1 ? b.leistungen : []}
                   offen={mangel} zu={() => setMangel(false)}
                   begehungId={b.id} leistungId={b.leistungen.length === 1 ? b.leistung?.id : null} />
      {leistungenWahl && <LeistungenDialog b={b} zu={() => setLeistungenWahl(false)}
                                           gesetzt={(neu) => qc.setQueryData(schluessel, { ...neu, meine_rolle: b.meine_rolle })} />}
      <AbschlussDialog b={b} offen={abschluss} zu={() => setAbschluss(false)} />
    </main>
  );
}

function AbschlussDialog({ b, offen, zu }: { b: Begehung; offen: boolean; zu: () => void }) {
  const qc = useQueryClient();
  const [hinweis, setHinweis] = useState("");
  const los = useMutation({
    mutationFn: () => schick<{ ok: boolean; schreiben: unknown; hinweis?: string }>(`/begehungen/${b.id}/abschluss`),
    onSuccess: (r) => {
      // Abschluss setzt Maengel auf „angezeigt“ und legt die Niederschrift ab — alle
      // Listen, die das zeigen, sind danach veraltet.
      for (const k of [["begehung", b.id], ["begehungen", b.projekt_id], ["maengel", b.projekt_id],
                       ["leistungen", b.projekt_id], ["schreiben", b.projekt_id]])
        qc.invalidateQueries({ queryKey: k });
      if (r.schreiben) zu();
      else setHinweis(r.hinweis || "Abgeschlossen, aber das PDF liegt noch nicht im Ausgang. Unten mit „Ablage nachholen“ erneut versuchen.");
    },
  });
  return (
    <Dialog offen={offen} zu={zu} titel="Niederschrift abschließen" fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={los.isPending} onClick={() => los.mutate()}>Abschließen</button>
    </>}>
      <p style={{ marginTop: 0 }}>Danach ist die Niederschrift unveränderlich und liegt als PDF im Ausgang.
        Die aufgeführten Mängel gelten als angezeigt.</p>
      {(b.art === "abnahme" || b.art === "teilabnahme") && b.ergebnis !== "verweigert" && (
        <p className="mute">Ab {datum(b.abnahme_wirkt_ab)} läuft die Frist für Mängelansprüche.</p>
      )}
      <Meldung fehler={los.error} />
      {hinweis && <div className="meldung">{hinweis}</div>}
    </Dialog>
  );
}

/** Wer beim Termin war (Rückmeldung 27.10.2026: war nur anzusehen). Wie die Teilnehmenden
 *  im Protokoll, dazu Entfernen und Gäste ohne Beteiligten — vor Ort steht oft ein
 *  Nachunternehmer oder Planer dabei, der nicht im Projekt geführt wird. */
function Anwesende({ b, offen, neuLaden }: { b: Begehung; offen: boolean; neuLaden: () => void }) {
  const beteiligte = useBeteiligte(b.projekt_id);
  const [neu, setNeu] = useState("");
  const [gast, setGast] = useState<{ name: string; firma: string } | null>(null);
  const status = useMutation({
    mutationFn: ({ id, s }: { id: string; s: string }) => aender(`/teilnahme/${id}`, { status: s }),
    onSuccess: neuLaden,
  });
  const raus = useMutation({ mutationFn: (id: string) => weg(`/teilnahme/${id}`), onSuccess: neuLaden });
  const dazu = useMutation({
    mutationFn: (rumpf: { beteiligter_id?: string; name?: string; firma?: string }) =>
      schick(`/begehungen/${b.id}/teilnahme`, rumpf),
    onSuccess: () => { setNeu(""); setGast(null); neuLaden(); },
  });
  const da = new Set(b.teilnahme.map((t) => t.beteiligter_id));
  const fehlen = (beteiligte.data ?? []).filter((x) => !da.has(x.id));
  return (
    <section className="karte">
      <div className="karte-kopf">
        <h2>Anwesend</h2>
        <span className="mute">{b.teilnahme.filter((t) => t.status === "anwesend").length} anwesend</span>
      </div>
      <div className="liste">
        {b.teilnahme.map((t) => (
          <div key={t.id} className="zeile" style={{ minHeight: 48, padding: "6px 4px" }}>
            <span className="nr">{t.kuerzel}</span>
            <div className="haupt"><div>{t.name || t.firma}</div>{t.name && <div className="mute">{t.firma}</div>}</div>
            {offen ? (
              <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <Auswahl style={{ width: "auto" }} value={t.status} aria-label={`Teilnahme ${t.name || t.firma}`}
                         onChange={(e) => status.mutate({ id: t.id, s: e.target.value })}>
                  {Object.entries(TEILNAHME).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Auswahl>
                <button className="knopf leise symbol" aria-label={`${t.name || t.firma} entfernen`}
                        onClick={() => raus.mutate(t.id)}><X size={16} /></button>
              </span>
            ) : <span className="chip">{TEILNAHME[t.status]}</span>}
          </div>
        ))}
        {b.teilnahme.length === 0 && <div className="leer">Noch niemand eingetragen.</div>}
      </div>
      {offen && (
        <div className="punkt-leiste">
          {fehlen.length > 0 && <>
            <Auswahl value={neu} onChange={(e) => setNeu(e.target.value)}>
              <option value="">Beteiligten hinzufügen …</option>
              {fehlen.map((x) => <option key={x.id} value={x.id}>{[x.firma, x.name].filter(Boolean).join(" · ")}</option>)}
            </Auswahl>
            <button className="knopf" disabled={!neu || dazu.isPending} onClick={() => dazu.mutate({ beteiligter_id: neu })}>
              <UserPlus size={16} /> hinzufügen</button>
          </>}
          <button className="knopf leise" onClick={() => setGast({ name: "", firma: "" })}><Plus size={16} /> Weitere Person</button>
        </div>
      )}
      <Meldung fehler={status.error ?? raus.error ?? dazu.error} />
      <Dialog offen={!!gast} zu={() => setGast(null)} titel="Weitere Person" fuss={<>
        <button className="knopf leise" onClick={() => setGast(null)}>Abbrechen</button>
        <button className="knopf haupt" disabled={!gast || !(gast.name.trim() || gast.firma.trim()) || dazu.isPending}
                onClick={() => gast && dazu.mutate(gast)}>Hinzufügen</button>
      </>}>
        <p className="mute" style={{ marginTop: 0 }}>Nur für diese Niederschrift – nicht im Adressbuch.</p>
        <div className="raster">
          <Feld titel="Name"><input className="eingabe" autoFocus value={gast?.name ?? ""}
                                    onChange={(e) => setGast((g) => g && { ...g, name: e.target.value })} /></Feld>
          <Feld titel="Firma / Funktion"><input className="eingabe" value={gast?.firma ?? ""}
                                                onChange={(e) => setGast((g) => g && { ...g, firma: e.target.value })} /></Feld>
        </div>
      </Dialog>
    </section>
  );
}

/** Leistungen im Entwurf nachtragen oder herausnehmen (Wanderung 022). Neu dazugekommene
 *  Auftragnehmer stehen danach als anwesend in der Liste. */
function LeistungenDialog({ b, zu, gesetzt }: { b: Begehung; zu: () => void; gesetzt: (neu: Begehung) => void }) {
  const leistungen = useLeistungen(b.projekt_id);
  const [ids, setIds] = useState(b.leistungen.map((l) => l.id));
  const abnahme = b.art === "abnahme" || b.art === "teilabnahme";
  const los = useMutation({
    mutationFn: () => api<Begehung>("PUT", `/begehungen/${b.id}/leistungen`, { leistung_ids: ids }),
    onSuccess: (neu) => { gesetzt(neu); zu(); },
  });
  return (
    <Dialog offen zu={zu} titel="Leistungen" fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={los.isPending || (abnahme && !ids.length)} onClick={() => los.mutate()}>Übernehmen</button>
    </>}>
      <p className="mute" style={{ marginTop: 0 }}>
        {abnahme ? "Alle Leistungen, die in diesem Termin abgenommen werden." : "Worauf sich der Termin bezieht."}
      </p>
      <LeistungenWahl leistungen={leistungen.data ?? []} gewaehlt={ids} setze={setIds} />
      <Meldung fehler={los.error} />
    </Dialog>
  );
}
